import { UseRecordsOptions } from "../../api/useRecords";
import { FieldExtraOption } from "../../types/fields.types";
import { filterUsersRoles } from "../../utils/filterUsersRoles";

export const sessionTriggerFields = [
	"trigger_session_status",
	"trigger_session_number",
	"trigger_sessions_service",
	"trigger_sessions_service_exclusion"
];
export const treatmentTriggerFields = ["trigger_treatment_status", "trigger_treatment_provided_services", "trigger_treatment_techs"];

export const triggerFieldsByCollection: Record<string, Array<string>> = {
	sessions: sessionTriggerFields,
	treatments: treatmentTriggerFields
};

export const allTriggerFields = [...sessionTriggerFields, ...treatmentTriggerFields];

// The two mutually-exclusive attribution fields, rendered by the AttributionColumn switcher.
export const attributionFields = ["assigned_users", "dynamic_assigned_user"];

// Choice values hidden per target collection (field → values to hide). Extend freely: another
// value, another collection, or another select field are each just one entry here.
export const hiddenChoicesByCollection: Record<string, Record<string, Array<string>>> = {
	treatments: {
		dynamic_assigned_user: ["technician"],
		dynamic_created_user: ["technician"]
	}
};

export const hasValue = (value: any) => {
	const isNonEmpty = Array.isArray(value) ? value.length > 0 : value != null && value !== "";
	return isNonEmpty;
};

const hideWhenEmpty = (fieldName: string) => (item: any) => !hasValue(item?.[fieldName]);

// getFieldExtraOption returns the FIRST entry per field, so an override for a field that already
// has an entry (assigned_users, trigger_sessions_service) must be merged into that entry — a second
// entry with the same field would be silently ignored.
export const mergeFieldExtraOptions = (
	baseOptions: Array<FieldExtraOption>,
	overridesByField: Record<string, FieldExtraOption>
) => {
	const mergedExisting = baseOptions.map((option) => {
		const override = option.field ? overridesByField[option.field] : undefined;
		if (!override) return option;
		return { ...option, ...override, hidden: { ...option.hidden, ...override.hidden } };
	});

	const existingFields = new Set(baseOptions.map((option) => option.field));
	const addedOverrides = Object.entries(overridesByField)
		.filter(([field]) => !existingFields.has(field))
		.map(([field, override]): FieldExtraOption => ({ field, ...override }));

	const allOptions = [...mergedExisting, ...addedOverrides];
	return allOptions;
};

// Shared config for the fixed-user M2O picker (assigned_users) — reused by the AttributionColumn.
export const assignedUsersExtraOption: FieldExtraOption = {
	field: "assigned_users",
	relatedTable: "directus_users",
	displayTemplate: "{{first_name}} {{last_name}}",
	filter: filterUsersRoles,
	sort: "first_name,last_name",
	fieldsQuery: "*.*",
	showM2OUserAvatar: true,
	enabled: true
};

const isDynamicTaskFormValid = (data: any) => {
	if (!hasValue(data.target_collection)) {
		return { valid: false, message: "Selecione a coleção alvo da tarefa." };
	}
	// The status of the selected collection is required; other collections have no status trigger.
	if (data.target_collection === "sessions" && !hasValue(data.trigger_session_status)) {
		return { valid: false, message: "Selecione o estado da sessão que dispara a tarefa." };
	}
	// M2O values arrive as a plain id or as the selected record, so compare by id.
	const relatedId = (value: any) => (typeof value === "object" ? value?.id : value);
	if (
		data.target_collection === "sessions" &&
		hasValue(data.trigger_sessions_service) &&
		relatedId(data.trigger_sessions_service) === relatedId(data.trigger_sessions_service_exclusion)
	) {
		return { valid: false, message: "O serviço da sessão e o serviço de exclusão não podem ser o mesmo." };
	}
	if (data.target_collection === "treatments" && !hasValue(data.trigger_treatment_status)) {
		return { valid: false, message: "Selecione o estado do tratamento que dispara a tarefa." };
	}
	const hasAttribution = hasValue(data.assigned_users) || hasValue(data.dynamic_assigned_user);
	if (!hasAttribution) {
		return { valid: false, message: "Defina a atribuição da tarefa (dinâmica ou fixa)." };
	}
	return { valid: true };
};

export const dynamicTasksOptions = {
	fieldsToRemove: {
		fieldNamesToRemove: ["id"],
		fieldTypesToRemove: []
	},

	groupsOptions: {
		fieldsWithExtraOptions: [
			assignedUsersExtraOption,
			{
				field: "participant_users",
				relatedTable: "directus_users",
				displayTemplate: "{{first_name}} {{last_name}}",
				filter: filterUsersRoles,
				sort: "first_name,last_name",
				fieldsQuery: "*.*",
				enabled: true
			},
			{
				field: "trigger_sessions_service",
				relatedTable: "services",
				displayTemplate: "{{name}}",
				sort: "sort_order",
				fieldsQuery: "*.*",
				enabled: true
			},
			{
				field: "trigger_sessions_service_exclusion",
				relatedTable: "services",
				displayTemplate: "{{name}}",
				sort: "sort_order",
				fieldsQuery: "*.*",
				enabled: true
			}
		],
		isFormValid: isDynamicTaskFormValid
	},

	dataGridOptions: {},

	extraOptions: {
		sort: "-sort"
	}
} as UseRecordsOptions;

export const dynamicTasksDetailGroupsOptions = {
	...dynamicTasksOptions.groupsOptions,
	fieldsWithExtraOptions: mergeFieldExtraOptions(dynamicTasksOptions.groupsOptions?.fieldsWithExtraOptions ?? [], {
		...Object.fromEntries(
			[...allTriggerFields, ...attributionFields].map((field) => [field, { hidden: { detailFn: hideWhenEmpty(field) } }])
		),
		// content is pulled out of its group and rendered at the end of the attribution column.
		content: { hidden: { detail: true } }
	})
};
